/**
 * 通知佇列的薄包裝（T09 立介面、T10 搬進獨立檔）。
 *
 * 職責：**只**管「訊息怎麼進佇列」。不碰 API、不碰訊息狀態。
 *
 * `setActionError` / `setNotice` 這兩個舊名字留成「寫進佇列」的薄包裝，
 * 三十幾個呼叫點不必同時改，行為已經是「多筆並存、可逐條關閉」：
 * - `setActionError(msg)` → error（sticky，留到被關掉或下一次動作成功）
 * - `setNotice(msg)` → info；成功的結果請自己在第二個參數標 `'success'`
 */
import { useCallback } from 'react';
import { useNotices } from '@chat-rea/console-ui';
import type { Notice, NoticeKind, PushNoticeInput } from '@chat-rea/console-ui';

export interface NoticeQueue {
  notices: Notice[];
  dismiss: (id: string) => void;
  /** 還有沒關掉的錯誤。 */
  hasError: boolean;
  push: (input: PushNoticeInput) => void;
  clearKind: (kind: NoticeKind) => void;
  setActionError: (message: string | null) => void;
  setNotice: (message: string | null, kind?: NoticeKind) => void;
}

export function useNoticeQueue(): NoticeQueue {
  const notices = useNotices();
  const pushNotice = notices.push;

  const setActionError = useCallback(
    (message: string | null) => {
      if (message) pushNotice({ kind: 'error', message });
    },
    [pushNotice],
  );

  const setNotice = useCallback(
    (message: string | null, kind: NoticeKind = 'info') => {
      if (message) pushNotice({ kind, message });
    },
    [pushNotice],
  );

  return {
    notices: notices.notices,
    dismiss: notices.dismiss,
    hasError: notices.hasError,
    push: pushNotice,
    clearKind: notices.clearKind,
    setActionError,
    setNotice,
  };
}
